// Pick list under a contact field (Kloepfer K7-34): while someone types a
// company or a name, show this company's contacts from its other jobs (click
// = whole card) and companies already in ConsLog (click = name only). Shared
// by the project Directory's Add contact and the dashboard's Key Contacts.
//
// The list lives on <body> with fixed positioning so a scrolling pop-up body
// never clips it.
//
//   attachContactSuggest(input, { url, kind: "company" | "name", strings,
//                                 enabled: () => bool, onPick(item, source) })
//   source is "other_jobs" or "conslog_company".

const DEBOUNCE_MS = 200

export function attachContactSuggest(input, opts) {
  if (!input || !opts.url || input.dataset.contactSuggest) return
  input.dataset.contactSuggest = "on"
  input.setAttribute("autocomplete", "off")

  const s = opts.strings || {}
  let list = null
  let items = []
  let active = -1
  let timer = null
  let aborter = null

  const onScroll = (e) => { if (!list?.contains(e.target)) close() }

  const close = () => {
    if (list) {
      window.removeEventListener("resize", place)
      window.removeEventListener("scroll", onScroll, true)
    }
    list?.remove()
    list = null
    items = []
    active = -1
  }

  const place = () => {
    if (!list) return
    const r = input.getBoundingClientRect()
    list.style.left = `${r.left}px`
    list.style.top = `${r.bottom + 4}px`
    list.style.width = `${Math.max(r.width, 340)}px`
  }

  const pick = (i) => {
    const it = items[i]
    if (!it) return
    close()
    opts.onPick?.(it.data, it.source)
  }

  const highlight = (text, words) => {
    let html = esc(text)
    words.forEach(w => {
      const ew = esc(w)
      if (!ew) return
      html = html.replace(new RegExp(`(${ew.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"), "<b>$1</b>")
    })
    return html
  }

  const render = (data, q) => {
    const contacts = data.contacts || []
    const companies = data.companies || []
    if (!contacts.length && !companies.length) return close()

    const words = q.split(/\s+/).filter(Boolean)
    items = []
    let html = ""
    if (contacts.length) {
      html += `<div class="px-3 py-1.5 text-[13px] font-medium text-gray-600 bg-gray-50 border-b border-gray-100">${esc(s.from_jobs)}</div>`
      contacts.forEach(c => {
        const i = items.push({ data: c, source: "other_jobs" }) - 1
        const jobs = c.jobs === 1 ? s.on_jobs_one : (s.on_jobs_other || "").replace("%{count}", c.jobs)
        const head = [c.name, c.company].filter(Boolean)
        html += `
          <div data-i="${i}" class="px-3 py-2 cursor-pointer hover:bg-blue-50">
            <div class="flex items-center gap-2 min-w-0">
              <span class="text-[13px] font-medium text-gray-800 truncate">${highlight(head[0] || "", words)}</span>
              ${head[1] ? `<span class="text-gray-300">&middot;</span><span class="text-[13px] text-gray-600 truncate">${highlight(head[1], words)}</span>` : ""}
            </div>
            <div class="text-[13px] text-gray-500 mt-0.5 flex flex-wrap items-center gap-x-3">
              ${c.purpose ? `<span class="text-amber-700">${esc(c.purpose)}</span>` : ""}
              ${c.phone ? `<span class="tabular-nums">${esc(c.phone)}</span>` : ""}
              <span class="text-gray-400">${esc(jobs)}</span>
            </div>
          </div>`
      })
    }
    if (companies.length) {
      html += `<div class="px-3 py-1.5 text-[13px] font-medium text-gray-600 bg-gray-50 border-y border-gray-100">${esc(s.in_conslog)} <span class="font-normal text-gray-400">· ${esc(s.name_only)}</span></div>`
      companies.forEach(c => {
        const i = items.push({ data: c, source: "conslog_company" }) - 1
        html += `
          <div data-i="${i}" class="px-3 py-2 flex items-center justify-between gap-2 cursor-pointer hover:bg-blue-50">
            <span class="text-[13px] text-gray-800 truncate">${highlight(c.name, words)}</span>
            ${c.place ? `<span class="text-[13px] text-gray-400 shrink-0">${esc(c.place)}</span>` : ""}
          </div>`
      })
    }
    html += `<div class="px-3 py-1.5 text-[13px] text-gray-400 border-t border-gray-100">${esc(s.not_here)}</div>`

    if (!list) {
      list = document.createElement("div")
      list.className = "bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden"
      list.style.position = "fixed"
      list.style.zIndex = "70"
      list.style.maxHeight = "360px"
      list.style.overflowY = "auto"
      // Keep focus in the field so the click lands before blur closes us.
      list.addEventListener("mousedown", e => e.preventDefault())
      list.addEventListener("click", e => {
        const row = e.target.closest("[data-i]")
        if (row) pick(parseInt(row.dataset.i, 10))
      })
      document.body.appendChild(list)
      window.addEventListener("resize", place)
      window.addEventListener("scroll", onScroll, true)
    }
    list.innerHTML = html
    active = -1
    place()
  }

  const lookup = async () => {
    const q = input.value.trim()
    if (q.length < 2 || (opts.enabled && !opts.enabled())) return close()
    aborter?.abort()
    aborter = new AbortController()
    try {
      const url = `${opts.url}?q=${encodeURIComponent(q)}&kind=${encodeURIComponent(opts.kind || "company")}`
      const resp = await fetch(url, { headers: { "Accept": "application/json" }, signal: aborter.signal })
      if (!resp.ok) return close()
      const data = await resp.json()
      if (input.value.trim() !== q || document.activeElement !== input) return
      render(data, q)
    } catch (e) {
      if (e.name !== "AbortError") close()
    }
  }

  const move = (step) => {
    if (!list || !items.length) return
    active = (active + step + items.length) % items.length
    list.querySelectorAll("[data-i]").forEach(el => {
      const on = parseInt(el.dataset.i, 10) === active
      el.classList.toggle("bg-blue-50", on)
      if (on) el.scrollIntoView({ block: "nearest" })
    })
  }

  input.addEventListener("input", () => {
    clearTimeout(timer)
    timer = setTimeout(lookup, DEBOUNCE_MS)
  })
  input.addEventListener("keydown", e => {
    if (!list) return
    if (e.key === "ArrowDown") { e.preventDefault(); move(1) }
    else if (e.key === "ArrowUp") { e.preventDefault(); move(-1) }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(active) }
    else if (e.key === "Escape") { e.stopPropagation(); close() }
  })
  input.addEventListener("blur", () => setTimeout(close, 120))
}

function esc(v) {
  return String(v ?? "").replace(/[&<>"']/g, ch => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]))
}
